import type { ReactNode } from 'react';

import type {
  Sender,
  SenderType
} from '@/features/sender-selection/types/sender';

/** Narrows `Sender` to the variant matching the given type. */
type SenderOf<T extends SenderType> = Extract<Sender, { type: T }>;

type Props<T extends SenderType> = {
  /** Current sender — decides whether the children render. */
  sender: Sender;
  /** The sender type for which the children should render. */
  type: T;
  /**
   * Rendered with the sender narrowed to the variant matching `type`
   * (e.g. grants access to `organizationId` for `SenderType.ORGANIZATION`).
   */
  children: (sender: SenderOf<T>) => ReactNode;
  /** Rendered when the sender type does not match (defaults to nothing). */
  fallback?: ReactNode;
};

/**
 * Renders `children` only when the current sender's type matches `type`.
 * Keeps sender-type conditionals out of the surrounding JSX, mirroring
 * `PostChannelTypeGuard`; the render-prop narrows the union for the caller.
 */
export const SenderGuard = <T extends SenderType>({
  sender,
  type,
  children,
  fallback
}: Props<T>) => (
  <>{sender.type === type ? children(sender as SenderOf<T>) : fallback}</>
);
